'use client';

import { useMemo, useContext } from 'react';
import { useSearchParams } from 'next/navigation';

import createStyles from '@guestyci/foundation/createStyles';
import TextField from '@guestyci/foundation/TextField';
import t from '@guestyci/localize/t.macro/t.macro';
import { Button } from '@guestyci/arc';

import { Link, usePathname } from 'i18n/routing';
import { WebsiteSettingsContext } from 'context/WebsiteSettingsContext';
import CurrencySelect from 'components/CurrencySelect';
import SelectLanguageDropdown from 'components/SelectLanguageDropdown';
import FullPageLoader from 'components/FullPageLoader';

const useStyles = createStyles(({ breakpoints: { create } }) => ({
  root: {
    background: ({ background }) => background || '#fff',
    height: 100,
    display: 'flex',
    padding: 20,
    alignItems: 'center',
    flexDirection: 'row',
    zIndex: 50,
    position: 'sticky',
    top: 0,
    [create('xs')]: {
      flexDirection: 'column',
      gap: 20,
    },
    [create('sm')]: {
      flexDirection: 'row',
    },
    '& *:focus-within': {
      '--tw-ring-color': ({ textColor }) => `${textColor} !important`,
    },
  },
  navigation: {
    marginLeft: 'auto',
    display: 'flex',
    justifyContent: 'flex-end',
    alignSelf: 'center',
    alignItems: 'center',
    width: 120,
    [create('xs')]: {
      gap: 10,
      justifyContent: 'center',
      width: '100%',
    },
    [create('sm')]: {
      justifyContent: 'flex-end',
    },
    [create('md')]: {
      gap: 30,
      width: 120,
    },
    '& > a': {
      fontSize: 14,
      color: ({ textColor }) => `${textColor || '#212121'} !important`,
    },
  },
  logo: {
    [create('xs')]: {
      marginLeft: 0,
      marginRight: 0,
      height: 30,
    },
    [create('md')]: {
      marginRight: -120,
      height: 60,
      width: 'auto',
    },
    [create('lg')]: {
      marginLeft: 'auto',
    },
    '& > img': {
      width: 'auto',
      height: '100%',
    },
  },
}));

const LogoWithOrWithourExternalWebsiteUrl = ({
  className,
  externalLinkingOn,
  externalWebsiteUrl,
  logoUrl,
  unifiedDomain,
}) => {
  if (unifiedDomain) {
    return (
      <a className={className} href="/" rel="noreferrer" aria-label={t('Home')}>
        <img src={logoUrl} alt={t('Home')} />
      </a>
    );
  }
  if (externalWebsiteUrl && externalLinkingOn) {
    return (
      <Button variant="plain-primary" className="gst-p-0" asChild>
        <a
          className={className}
          href={externalWebsiteUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={t('Home opens in a new tab')}
        >
          <img src={logoUrl} alt={t('Home opens in a new tab')} />
        </a>
      </Button>
    );
  }
  return (
    <Button variant="plain-primary" className="gst-p-0" asChild>
      <Link
        className={className}
        href='/'
        aria-label={t('Home')}
      >
        <img src={logoUrl} aria-label={t('Home')} />
      </Link>
    </Button>
  );
};

const Header = () => {
  const pathname = usePathname();
  const {
    contentConfiguration: {
      logo: logoImage = '',
      colorTheme: { colors = {} } = {},
      externalWebsiteUrl = null,
      sections,
    } = {},
    displayOptions: { externalLinkingOn = false, shouldShowContactUsForm = false } = {},
    enginesData: { unifiedDomain = false } = {},
    isLoading,
  } = useContext(WebsiteSettingsContext);

  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const { root, navigation, logo } = useStyles({
    background: colors?.headerBackground,
    textColor: colors?.headerLink,
  });

  const withContact = useMemo(() => !!sections?.CONTACT?.active, [sections]);
  const withTerms = useMemo(() => !!sections?.TERMS?.active, [sections]);

  if (isLoading) {
    return <FullPageLoader />;
  }

  return (
    <header className={root}>
      <LogoWithOrWithourExternalWebsiteUrl
        externalWebsiteUrl={externalWebsiteUrl}
        externalLinkingOn={externalLinkingOn}
        logoUrl={logoImage}
        className={logo}
        search={search}
        unifiedDomain={unifiedDomain}
      />
      <div className={navigation} role="navigation" aria-label={t('Main menu')}>
        <SelectLanguageDropdown />
        <CurrencySelect />
        {(shouldShowContactUsForm || withContact) && (
          <Button variant="plain-primary" className="gst-p-0 gst-font-normal" asChild>
            <Link href="/contact" aria-current={pathname.includes('/contact') ? 'page' : false}>
              <TextField>{t('Contact')}</TextField>
            </Link>
          </Button>
        )}
        {withTerms && (
          <Button variant="plain-primary" className="gst-p-0 gst-font-normal" asChild>
            <Link href="/terms" aria-current={pathname.includes('/terms') ? 'page' : false}>
              <TextField>{t('Terms')}</TextField>
            </Link>
          </Button>
        )}
      </div>
    </header>
  );
};

export default Header;
